import { countries, CountryCode } from "@redotech/locale/countries";
import { USAProvinceCode, usaProvinces } from "@redotech/locale/provinces";
import { useHandler } from "@redotech/react-util/hook";
import { OriginLocationSegment } from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";

export type AddressType = OriginLocationSegment["originAddress"];

export const AddressEditModal = memo(function AddressEditModal({
  address,
  saveAddress,
  isOpen,
  onClose,
}: {
  address: AddressType;
  isOpen: boolean;
  saveAddress: (address: AddressType) => void;
  onClose: () => void;
}) {
  const [localAddress, setLocalAddress] = useState<AddressType>(address);

  const handleAddress1Change = useHandler((value: string) => {
    setLocalAddress({ ...localAddress, address1: value });
  });

  const handleAddress2Change = useHandler((value: string) => {
    setLocalAddress({ ...localAddress, address2: value });
  });

  const handleCityChange = useHandler((value: string) => {
    setLocalAddress({ ...localAddress, city: value });
  });

  const handlePostalCodeChange = useHandler((value: string) => {
    setLocalAddress({ ...localAddress, postalCode: value });
  });

  const handleCountrySelected = useHandler(
    ({ item }: { item: RedoListItem<CountryCode> }) => {
      setLocalAddress({ ...localAddress, country: item.value });
    },
  );

  const handleProvinceChange = useHandler((value: string) => {
    setLocalAddress({ ...localAddress, province: value });
  });

  const handleUsProvinceSelected = useHandler(
    ({ item }: { item: RedoListItem<USAProvinceCode> }) => {
      setLocalAddress({ ...localAddress, province: item.value });
    },
  );

  const handleSave = useHandler(() => {
    saveAddress(localAddress);
  });

  const countryOptions: TextRedoListItem<CountryCode>[] = countries.map(
    (country) => ({
      id: country.code,
      type: "text",
      text: country.name,
      value: country.code,
    }),
  );

  const isUnitedStates = useMemo(
    () => localAddress.country === "US",
    [localAddress.country],
  );

  const provinceOptions: TextRedoListItem<USAProvinceCode>[] = usaProvinces.map(
    (province) => ({
      id: province.code,
      type: "text",
      text: province.name,
      value: province.code,
    }),
  );

  return (
    <RedoModal
      isOpen={isOpen}
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Save", onClick: handleSave }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Edit address"
    >
      <Flex dir="column" gap="xl">
        <RedoSingleSelectDropdownInput<CountryCode>
          label="Country"
          options={countryOptions}
          optionSelected={handleCountrySelected}
          placeholder="Select country"
          selectedItem={countryOptions.find(
            (option) => option.value === localAddress.country,
          )}
        />
        <RedoTextInput
          fullWidth
          label="Address"
          placeholder="Enter street address"
          setValue={handleAddress1Change}
          size="sm"
          value={localAddress.address1 || ""}
        />
        <RedoTextInput
          fullWidth
          label="Address line 2"
          placeholder="Apt, suite, etc."
          setValue={handleAddress2Change}
          size="sm"
          value={localAddress.address2 || ""}
        />
        <Flex dir="row" gap="md">
          <Flex grow={1}>
            <RedoTextInput
              fullWidth
              label="City"
              placeholder="Enter city"
              required
              setValue={handleCityChange}
              size="sm"
              value={localAddress.city || ""}
            />
          </Flex>
          <Flex grow={1}>
            {isUnitedStates ? (
              <RedoSingleSelectDropdownInput<USAProvinceCode>
                label="Province"
                options={provinceOptions}
                optionSelected={handleUsProvinceSelected}
                placeholder="Select province/state"
                selectedItem={provinceOptions.find(
                  (option) => option.value === localAddress.province,
                )}
              />
            ) : (
              <RedoTextInput
                fullWidth
                label="Province"
                placeholder="Enter province/state"
                setValue={handleProvinceChange}
                size="sm"
                value={localAddress.province || ""}
              />
            )}
          </Flex>
          <Flex grow={1}>
            <RedoTextInput
              fullWidth
              label="Postal code"
              placeholder="Enter postal code"
              required
              setValue={handlePostalCodeChange}
              size="sm"
              value={localAddress.postalCode || ""}
            />
          </Flex>
        </Flex>
      </Flex>
    </RedoModal>
  );
});
